import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Mail, 
  Phone, 
  MessageSquare, 
  Eye,
  Copy,
  Save,
  X
} from 'lucide-react';
import { communicationService } from '../../services/api';
import { MessageTemplate } from '../../types';

export default function TemplateManagement() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const queryClient = useQueryClient();

  // Récupérer les templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => communicationService.getTemplates().then(res => res.data)
  });

  const templates = templatesData?.results || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<MessageTemplate>) => communicationService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-templates'] });
      setIsModalOpen(false);
      setSelectedTemplate(null);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<MessageTemplate> }) => 
      communicationService.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-templates'] });
      setIsModalOpen(false);
      setSelectedTemplate(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => communicationService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-templates'] });
    }
  });

  const handleCreate = () => {
    setSelectedTemplate(null);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  const handleView = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    setIsViewMode(true);
    setIsModalOpen(true);
  };

  const handleDuplicate = (template: MessageTemplate) => {
    const { id, ...templateWithoutId } = template;
    const newTemplate = {
      ...templateWithoutId,
      name: `${template.name} (Copie)`
    };
    setSelectedTemplate(newTemplate as MessageTemplate);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  const handleDelete = async (template: MessageTemplate) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le template "${template.name}" ?`)) {
      deleteMutation.mutate(template.id);
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'appointment_reminder': 'bg-blue-100 text-blue-800',
      'appointment_confirmation': 'bg-green-100 text-green-800',
      'payment_reminder': 'bg-red-100 text-red-800',
      'birthday_wishes': 'bg-purple-100 text-purple-800',
      'welcome_patient': 'bg-yellow-100 text-yellow-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const templateTypes = [
    { value: 'appointment_reminder', label: 'Rappel de RDV' },
    { value: 'appointment_confirmation', label: 'Confirmation de RDV' },
    { value: 'appointment_cancellation', label: 'Annulation de RDV' },
    { value: 'payment_reminder', label: 'Rappel de paiement' },
    { value: 'welcome_patient', label: 'Bienvenue patient' },
    { value: 'birthday_wishes', label: 'Vœux d\'anniversaire' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Templates de messages</h2>
          <p className="text-gray-600">Gérez vos modèles de messages pour les communications</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau template
        </button>
      </div>

      {/* Liste des templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {templates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">Aucun template</p>
            <p>Créez votre premier template de message</p>
            <button
              onClick={handleCreate}
              className="mt-4 inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Créer un template
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(template.template_type)}`}>
                        {template.template_type_display}
                      </span>
                      {!template.is_active && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          Inactif
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-3">{template.subject}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        {template.email_enabled && <Mail className="h-4 w-4 text-blue-500" />}
                        {template.sms_enabled && <Phone className="h-4 w-4 text-purple-500" />}
                        {template.whatsapp_enabled && <MessageSquare className="h-4 w-4 text-green-500" />}
                      </div>
                      <span>•</span>
                      <span>Créé le {new Date(template.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleView(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Voir"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDuplicate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Dupliquer"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Supprimer"
                      disabled={deleteMutation.isLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de création/édition */}
      {isModalOpen && (
        <TemplateModal
          template={selectedTemplate}
          isViewMode={isViewMode}
          templateTypes={templateTypes}
          onSave={(data) => {
            if (selectedTemplate?.id) {
              updateMutation.mutate({ id: selectedTemplate.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedTemplate(null);
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      )}
    </div>
  );
}

// Modal pour créer/éditer les templates
interface TemplateModalProps {
  template: MessageTemplate | null;
  isViewMode: boolean;
  templateTypes: Array<{ value: string; label: string }>;
  onSave: (data: Partial<MessageTemplate>) => void;
  onClose: () => void;
  isLoading: boolean;
}

function TemplateModal({ template, isViewMode, templateTypes, onSave, onClose, isLoading }: TemplateModalProps) {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    template_type: template?.template_type || '' as MessageTemplate['template_type'] | '',
    subject: template?.subject || '',
    content: template?.content || '',
    email_enabled: template?.email_enabled ?? true,
    sms_enabled: template?.sms_enabled ?? false,
    whatsapp_enabled: template?.whatsapp_enabled ?? false,
    is_active: template?.is_active ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isViewMode && formData.template_type) {
      onSave({
        ...formData,
        template_type: formData.template_type as MessageTemplate['template_type']
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {isViewMode ? 'Aperçu du template' : template?.id ? 'Modifier le template' : 'Nouveau template'}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Fermer">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du template</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 text-gray-600 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-50"
                    placeholder="Nom du template"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="template-type-select" className="block text-sm font-medium text-gray-700 mb-2">Type de template</label>
                  <select
                    id="template-type-select"
                    value={formData.template_type}
                    onChange={(e) => setFormData({ ...formData, template_type: e.target.value as MessageTemplate['template_type'] | '' })}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-600 focus:border-teal-500 disabled:bg-gray-50"
                    required
                  >
                    <option value="">Sélectionner un type</option>
                    {templateTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sujet</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-600 focus:border-teal-500 disabled:bg-gray-50"
                    placeholder="Sujet du message"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contenu du message</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    disabled={isViewMode}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-800 focus:border-teal-500 disabled:bg-gray-50"
                    placeholder="Contenu du message (utilisez {{variable}} pour les variables dynamiques)"
                    required
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    <p className="font-medium mb-1">Variables disponibles:</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{patient_name}}`}</span> - Nom du patient
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{clinic_name}}`}</span> - Nom de la clinique
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{appointment_date}}`}</span> - Date du RDV
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{appointment_time}}`}</span> - Heure du RDV
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{doctor_name}}`}</span> - Nom du médecin
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{motif}}`}</span> - Motif du RDV
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{invoice_number}}`}</span> - N° facture
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{amount_due}}`}</span> - Montant dû
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{due_date}}`}</span> - Date d'échéance
                      </div>
                      <div>
                        <span className="font-mono bg-gray-100 px-1 rounded">{`{{days_overdue}}`}</span> - Jours de retard
                      </div>
                    </div>
                    <p className="mt-2 text-xs italic">
                      Utilisez la syntaxe {`{{variable}}`} dans le contenu de vos messages
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Canaux de communication</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.email_enabled}
                        onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })}
                        disabled={isViewMode}
                        className="rounded border-gray-300 text-blue-600 focus:ring-teal-500"
                      />
                      <Mail className="h-4 w-4 ml-2 mr-1 text-blue-500" />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.sms_enabled}
                        onChange={(e) => setFormData({ ...formData, sms_enabled: e.target.checked })}
                        disabled={isViewMode}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <Phone className="h-4 w-4 ml-2 mr-1 text-purple-500" />
                      <span className="text-sm text-gray-700">SMS</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.whatsapp_enabled}
                        onChange={(e) => setFormData({ ...formData, whatsapp_enabled: e.target.checked })}
                        disabled={isViewMode}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <MessageSquare className="h-4 w-4 ml-2 mr-1 text-green-500" />
                      <span className="text-sm text-gray-700">WhatsApp</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      disabled={isViewMode}
                      className="rounded border-gray-300 text-blue-600 focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Template actif</span>
                  </label>
                </div>
              </div>
            </div>

            {!isViewMode && (
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50 sm:ml-3 sm:w-auto"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {template?.id ? 'Modifier' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Annuler
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}